import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import {
  gremlinCommit,
  gremlinPreview,
  type GremlinCommitResponse,
  type GremlinPreviewResponse,
  type LoadMode,
} from '@/lib/api';
import { ConfirmGraphReplaceModal } from '@/components/ConfirmGraphReplaceModal';
import { GraphSelector } from '@/components/GraphSelector';
import { invalidateAfterDataWrite } from '@/lib/invalidate';
import { useMaxLoadBytes } from '@/lib/usePages';
import { cn } from '@/lib/utils';
import { messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';

// ---------------------------------------------------------------------------
// Gremlin JSON graph import (RDF backend). Upload the two flat JSON arrays a
// Gremlin g.V()/g.E() traversal produces (from any TinkerPop-compatible source);
// the server lowers them via the shared to_statements pass and writes RDF
// (rdf:type / rdfs:label / dc:identifier + edge predicates under a base
// namespace) to a target graph. No column mapping — the graph shape is fixed.
// Preview → Commit.
// ---------------------------------------------------------------------------

type Step = 'load' | 'preview' | 'done';
const DEFAULT_NAMESPACE = 'http://data.local/gremlin#';
const DEFAULT_GRAPH = 'urn:tres:data';

export function GremlinImportPanel() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? '';
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('load');
  const [vertices, setVertices] = useState<unknown[] | null>(null);
  const [edges, setEdges] = useState<unknown[] | null>(null);
  const [verticesName, setVerticesName] = useState('');
  const [edgesName, setEdgesName] = useState('');
  const [namespace, setNamespace] = useState(DEFAULT_NAMESPACE);
  const [normalize, setNormalize] = useState(false);
  const [graph, setGraph] = useState(DEFAULT_GRAPH);
  const [mode, setMode] = useState<LoadMode>('append');
  const [confirmReplace, setConfirmReplace] = useState(false);

  const [preview, setPreview] = useState<GremlinPreviewResponse | null>(null);
  const [result, setResult] = useState<GremlinCommitResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const maxLoadBytes = useMaxLoadBytes().data ?? null;

  async function readJsonArray(
    file: File,
    setArr: (a: unknown[]) => void,
    setName: (n: string) => void,
  ) {
    setError(null);
    if (maxLoadBytes !== null && file.size > maxLoadBytes) {
      setError(`${file.name} exceeds this instance's maximum upload size.`);
      return;
    }
    try {
      const parsed = JSON.parse(await file.text());
      if (!Array.isArray(parsed)) {
        setError(`${file.name} must be a JSON array (a Gremlin g.V()/g.E() export).`);
        return;
      }
      setArr(parsed);
      setName(`${file.name} (${parsed.length.toLocaleString()} rows)`);
    } catch {
      setError(`${file.name} is not valid JSON.`);
    }
  }

  async function runPreview() {
    setBusy(true);
    setError(null);
    const res = await gremlinPreview(
      { vertices: vertices ?? [], edges: edges ?? [], namespace, normalize_predicates: normalize },
      token,
    );
    setBusy(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setPreview(res.data);
    setStep('preview');
  }

  async function runCommit() {
    setConfirmReplace(false);
    setBusy(true);
    setError(null);
    const res = await gremlinCommit(
      {
        vertices: vertices ?? [],
        edges: edges ?? [],
        namespace,
        normalize_predicates: normalize,
        graph,
        mode,
      },
      token,
    );
    setBusy(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setResult(res.data);
    setStep('done');
    invalidateAfterDataWrite(queryClient);
  }

  function reset() {
    setStep('load');
    setVertices(null);
    setEdges(null);
    setVerticesName('');
    setEdgesName('');
    setPreview(null);
    setResult(null);
    setError(null);
  }

  const canPreview = (vertices?.length ?? 0) > 0 || (edges?.length ?? 0) > 0;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Import a Gremlin JSON graph export — the two flat JSON arrays a{' '}
        <code>g.V()</code> (vertices) and <code>g.E()</code> (edges) traversal produces, from any
        TinkerPop-compatible source. Each vertex becomes a typed entity with its label and original id;
        each edge becomes a relationship. No re-keying.
      </p>

      {error && (
        <div className={messageClass('warning')}>
          {error}
        </div>
      )}

      {step === 'load' && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Vertices JSON (g.V())</span>
              <input
                type="file"
                accept=".json,application/json"
                onChange={(e) =>
                  e.target.files?.[0] && readJsonArray(e.target.files[0], setVertices, setVerticesName)
                }
              />
              {verticesName && <span className="mt-1 block text-xs text-muted-foreground">{verticesName}</span>}
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Edges JSON (g.E())</span>
              <input
                type="file"
                accept=".json,application/json"
                onChange={(e) =>
                  e.target.files?.[0] && readJsonArray(e.target.files[0], setEdges, setEdgesName)
                }
              />
              {edgesName && <span className="mt-1 block text-xs text-muted-foreground">{edgesName}</span>}
            </label>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">Base namespace (IRI)</span>
            <input
              type="text"
              value={namespace}
              onChange={(e) => setNamespace(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1"
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              Minted terms sit under this IRI. A separator (#) is appended if missing.
            </span>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={normalize} onChange={(e) => setNormalize(e.target.checked)} />
            Normalize predicate casing (fold <code>MANAGES</code> / <code>manages</code> into one)
          </label>

          <button
            type="button"
            disabled={!canPreview || busy}
            onClick={runPreview}
            className={cn(
              'rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground',
              (!canPreview || busy) && 'opacity-50',
            )}
          >
            {busy ? 'Analyzing…' : 'Preview'}
          </button>
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="space-y-4">
          <ReportGrid r={preview.report} extra={{ 'triples total': preview.triples_total }} />
          <div>
            <span className="mb-1 block text-sm font-medium">Sample triples</span>
            <pre className="max-h-64 overflow-auto rounded border border-border bg-muted/30 p-2 text-xs">
              {preview.sample_triples.join('\n')}
            </pre>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="text-sm">
              <span className="mb-1 block font-medium">Target graph</span>
              <GraphSelector
                value={graph}
                onChange={setGraph}
                allowCreate
                defaultBare={DEFAULT_GRAPH}
                storageKey="graphSelector.gremlinImport"
                label=""
              />
            </div>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Mode</span>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as LoadMode)}
                className="w-full rounded border border-border bg-background px-2 py-1"
              >
                <option value="append">Append to graph</option>
                <option value="replace">Replace graph (drop first)</option>
              </select>
            </label>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => (mode === 'replace' ? setConfirmReplace(true) : void runCommit())}
              className={cn(
                'rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground',
                busy && 'opacity-50',
              )}
            >
              {busy ? 'Importing…' : `Import ${preview.triples_total.toLocaleString()} triples`}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setStep('load')}
              className={buttonVariants({ variant: 'secondary', size: 'lg' })}
            >
              Back
            </button>
          </div>
        </div>
      )}

      {confirmReplace && (
        <ConfirmGraphReplaceModal
          graph={graph}
          what="this Gremlin export"
          onCancel={() => setConfirmReplace(false)}
          onConfirm={() => void runCommit()}
        />
      )}

      {step === 'done' && result && (
        <div className="space-y-4">
          <div className={messageClass('outcome')}>
            Imported <strong>{result.triples_written.toLocaleString()}</strong> triples into{' '}
            <code>{result.graph}</code>
            {result.replaced ? ' (replaced)' : ''}.
          </div>
          <ReportGrid r={result.report} />
          <button type="button" onClick={reset} className="rounded border border-border px-4 py-2 text-sm">
            Import another
          </button>
        </div>
      )}
    </div>
  );
}

function ReportGrid({
  r,
  extra,
}: {
  r: GremlinPreviewResponse['report'];
  extra?: Record<string, number>;
}) {
  const cells: [string, number][] = [
    ['vertices', r.vertices_in],
    ['edges', r.edges_in],
    ['statements', r.statements_out],
    ['distinct predicates', r.distinct_predicates],
    ['type conflicts', r.type_conflicts],
    ['dangling endpoints', r.dangling_endpoints],
    ['slug collisions', r.slug_collisions],
    ...Object.entries(extra ?? {}),
  ];
  return (
    <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
      {cells.map(([k, v]) => (
        <div key={k} className="rounded border border-border px-3 py-2">
          <div className="text-lg font-semibold">{v.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">{k}</div>
        </div>
      ))}
    </div>
  );
}
